import { useEffect, useMemo, useState } from "react";

import {
  faCheck,
  faCircleNotch,
  faSparkles,
} from "@fortawesome/pro-regular-svg-icons";
import { isAxiosError } from "axios";
import { m } from "framer-motion";
import { observer } from "mobx-react-lite";
import { v4 as uuidv4 } from "uuid";

import { PropertyValue, Schema } from "src/api/flowTypes";
import { useFlowVersion } from "src/api/flowVersionQueries";
import { useAutoMapping, useFetchAutoMapping } from "src/auto-mapping/queries";
import { Button } from "src/base-components/Button";
import {
  AutoMappingField,
  AutoMappingFieldTypeEnum,
  AutoMappingResponse,
  Context,
} from "src/clients/copilot";
import { FieldMapping } from "src/clients/flow-api";
import { useFirstDataSample } from "src/copilot/useFirstDataSample";
import { Icon } from "src/design-system/Icon";
import { Tooltip } from "src/design-system/Tooltip";
import {
  NoCompletionDataAvailable,
  useNodeCompletionData,
} from "src/nodeEditor/api/queries";
import { useNodeDetailsSidePaneContext } from "src/nodeEditor/NodeDetailsSidePane";
import { useAuthoringContext } from "src/router/routerContextHooks";
import { useGraphStore } from "src/store/StoreProvider";
import { GraphTransform } from "src/utils/GraphUtils";
import { logger } from "src/utils/logger";
import { isFlowNode, isLoopNode, isParentNode } from "src/utils/predicates";
import { toastError } from "src/utils/toastError";

type AutomapButtonProps = {
  type: "flow" | "flow_loop";
  nodeId: string;
  disabled?: boolean;
  tooltipTitle?: string;
  onAutoMapping?: (response: AutoMappingResponse) => void;
};

const getDataType = (property: PropertyValue): string => {
  if ("$ref" in property) {
    return property.$ref ?? "any";
  }

  if (!Array.isArray(property.type)) {
    return property.type ?? "any";
  }

  // to differentiate between enum and any, we check if the property has an enum key
  if ("enum" in property) {
    return "enum";
  }

  if (property.type.length === 1) {
    return property.type[0];
  }

  return "any";
};

const extractFilledFields = ({
  mappings,
  isOutput = false,
}: {
  mappings: Record<string, FieldMapping>;
  isOutput?: boolean;
}) => {
  return Object.keys(mappings)
    .map((key) => {
      if (isOutput) {
        return mappings[key].destination ? mappings[key].source : null;
      }

      return mappings[key].source ? mappings[key].destination : null;
    })
    .filter(Boolean) as string[];
};

const buildFields = (
  inputSchema: Schema | undefined,
  outputSchema: Schema | undefined,
  filledFields: {
    input: string[];
    output: string[];
  },
) => {
  const inputFields = inputSchema
    ? (Object.keys(inputSchema.properties)
        .map((field) => {
          if (filledFields.input.includes(field)) {
            return null;
          }

          return {
            name: field,
            data_type: getDataType(inputSchema.properties[field]),
            type: AutoMappingFieldTypeEnum.DESTINATION,
          };
        })
        .filter(Boolean) as AutoMappingField[])
    : [];

  const outputFields = outputSchema
    ? (Object.keys(outputSchema.properties)
        .map((field) => {
          if (filledFields.output.includes(field)) {
            return null;
          }

          return {
            name: field,
            data_type: getDataType(outputSchema.properties[field]),
            type: AutoMappingFieldTypeEnum.SOURCE,
          };
        })
        .filter(Boolean) as AutoMappingField[])
    : [];

  return [...inputFields, ...outputFields];
};

const AnimatedCheckIcon = () => (
  <m.span
    animate={{ opacity: 1, scale: 1 }}
    exit={{ opacity: 0, scale: 0.5 }}
    initial={{ opacity: 0, scale: 0.5 }}
    transition={{ duration: 0.2 }}
  >
    <Icon variant="success" icon={faCheck} size="2xs" />
  </m.span>
);

export const AutomapButton = observer(
  ({
    disabled = false,
    type,
    nodeId,
    onAutoMapping,
    tooltipTitle = "",
  }: AutomapButtonProps) => {
    const { requestPersistentLock, releasePersistentLock } =
      useNodeDetailsSidePaneContext();
    const { version } = useAuthoringContext();
    const [isSuccess, setIsSuccess] = useState(false);
    const { mutateAsync: askAutoMap, isPending: isAskingAutoMap } =
      useAutoMapping();
    const { mutateAsync: fetchAutoMapping, isPending: isFetchingAutoMapping } =
      useFetchAutoMapping();
    const { data: nodeTypingData } = useNodeCompletionData(
      version.id,
      nodeId,
      type === "flow_loop",
    );
    const { nodes } = useGraphStore();
    const node = nodes.get(nodeId) ?? null;
    const { data: childFlowVersion } = useFlowVersion(
      node && isParentNode(node) ? node?.data.child_flow_version_id : undefined,
    );

    const dataSample = useFirstDataSample({ node });
    const isPending = isAskingAutoMap || isFetchingAutoMapping;

    const totalFields = useMemo(() => {
      if (node && isFlowNode(node)) {
        return (
          Object.keys(node.data.input_mappings).length +
          Object.keys(node.data.output_mappings).length
        );
      }

      if (node && isLoopNode(node)) {
        return Object.keys(node.data.input_mappings).length;
      }

      return 0;
    }, [node]);

    const filledFields = useMemo(() => {
      if (node && isFlowNode(node)) {
        return {
          input: extractFilledFields({ mappings: node.data.input_mappings }),
          output: extractFilledFields({
            mappings: node.data.output_mappings,
            isOutput: true,
          }),
        };
      }

      if (node && isLoopNode(node)) {
        return {
          input: extractFilledFields({ mappings: node.data.input_mappings }),
          output: [],
        };
      }

      return {
        input: [],
        output: [],
      };
    }, [node]);

    const handleClick = async () => {
      if (isPending) {
        return;
      }

      requestPersistentLock();
      setIsSuccess(false);

      const request = {
        fields: buildFields(
          childFlowVersion?.input_schema,
          childFlowVersion?.output_schema,
          filledFields,
        ),
        context: {
          type: type,
          data_sample: dataSample,
          input_schema: version.input_schema,
          output_schema: version.output_schema,
          data_typing:
            nodeTypingData === NoCompletionDataAvailable
              ? undefined
              : nodeTypingData,
          node_definition: node ? GraphTransform.nodeReverseMapper(node) : null,
        } as Context,
        id: uuidv4(),
      };

      let response: AutoMappingResponse | undefined;

      try {
        response = await askAutoMap(request);
      } catch (e) {
        // if the request failed due to a 503 timeout, we need start polling for completion
        if (isAxiosError(e)) {
          try {
            response = await fetchAutoMapping(request.id);
          } catch (e) {
            logger.error(e);
            toastError("Failed to fetch auto-mapping");
          }
        } else {
          toastError("Failed to fetch auto-mapping");
        }
      }

      if (onAutoMapping && response) {
        onAutoMapping(response);
        setIsSuccess(true);
        setTimeout(() => {
          setIsSuccess(false);
        }, 5000);
      }
      releasePersistentLock();
    };

    useEffect(() => releasePersistentLock, [releasePersistentLock]);

    const isAllFieldsFilled =
      totalFields > 0 &&
      totalFields === filledFields.input.length + filledFields.output.length;

    return (
      <Tooltip
        disabled={!tooltipTitle}
        placement="left"
        title={tooltipTitle}
        asChild
      >
        <Button
          dataLoc="automap-button"
          disabled={disabled || isAllFieldsFilled}
          htmlType="button"
          size="sm"
          variant="secondary"
          onClick={handleClick}
        >
          <div className="flex items-center gap-1 overflow-hidden">
            {isSuccess ? (
              <AnimatedCheckIcon />
            ) : (
              <Icon
                variant={disabled || isAllFieldsFilled ? "disabled" : "default"}
                icon={isPending ? faCircleNotch : faSparkles}
                size="2xs"
                spin={isPending}
              />
            )}
            <span className="truncate pr-1">Auto-map fields</span>
          </div>
        </Button>
      </Tooltip>
    );
  },
);
